import { ReactNode, useEffect } from 'react';
import { useMobileStore } from '../../store/mobileStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import BottomNav from './BottomNav';

interface MobileLayoutProps {
  children: ReactNode;
}

function MobileLayout({ children }: MobileLayoutProps) {
  const { updateDeviceInfo } = useMobileStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const bottomNavPadding = 'calc(56px + env(safe-area-inset-bottom, 0px))';

  // Update device info on mount and when window resizes
  useEffect(() => {
    updateDeviceInfo();

    const updateAppHeight = () => {
      if (typeof window === 'undefined') return;
      // Use visualViewport if available (better for mobile), fallback to innerHeight
      const height = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${height}px`);
    };

    const handleResize = () => {
      updateDeviceInfo();
      updateAppHeight();
    };

    const handleOrientationChange = () => {
      // Small delay to ensure orientation change is complete
      setTimeout(updateDeviceInfo, 100);
      setTimeout(updateAppHeight, 100);
    };

    const handleVisualViewportChange = () => {
      // Handle viewport changes on mobile (address bar show/hide)
      updateAppHeight();
    };

    updateAppHeight();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Listen for visual viewport changes (mobile browsers)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, [updateDeviceInfo]);

  return (
    <div
      className={`flex flex-col ${tokens.bg.gradient} safe-area-inset-top safe-area-inset-left safe-area-inset-right transition-colors duration-300`}
      style={{ height: 'var(--app-height, 100vh)' }}
    >
      {/* Main content area */}
      <main
        className="flex-1 overflow-hidden"
        style={{ paddingBottom: bottomNavPadding }}
      >
        {children}
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default MobileLayout;
